import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/Button';

export type DrawerSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface SlideOverDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;
  fullPageUrl?: string;
  size?: DrawerSize;
  widthClass?: string;
  children: React.ReactNode;
}

const SIZE_WIDTH_MAP: Record<DrawerSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-full sm:max-w-xl md:max-w-2xl lg:max-w-[620px] xl:max-w-[650px]',
  lg: 'max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-[780px]',
  xl: 'max-w-full sm:max-w-3xl md:max-w-4xl lg:max-w-[900px]',
  full: 'max-w-full',
};

export const SlideOverDrawer: React.FC<SlideOverDrawerProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  badge,
  fullPageUrl,
  size = 'md',
  widthClass,
  children,
}) => {
  const navigate = useNavigate();

  // Resolved width class: custom widthClass takes precedence, otherwise use size preset (default 'md' ~ 560px)
  const resolvedWidth = widthClass || SIZE_WIDTH_MAP[size];

  // Preserve content during exit animation so it doesn't flash empty when parent unmounts child
  const cachedContentRef = useRef({
    children,
    title,
    subtitle,
    badge,
    fullPageUrl,
  });

  if (isOpen) {
    cachedContentRef.current = {
      children: children ?? cachedContentRef.current.children,
      title: title ?? cachedContentRef.current.title,
      subtitle: subtitle ?? cachedContentRef.current.subtitle,
      badge: badge ?? cachedContentRef.current.badge,
      fullPageUrl: fullPageUrl ?? cachedContentRef.current.fullPageUrl,
    };
  }

  const activeChildren = isOpen ? children : (children ?? cachedContentRef.current.children);
  const activeTitle = isOpen ? title : (title || cachedContentRef.current.title);
  const activeSubtitle = isOpen ? subtitle : (subtitle || cachedContentRef.current.subtitle);
  const activeBadge = isOpen ? badge : (badge || cachedContentRef.current.badge);
  const activeFullPageUrl = isOpen ? fullPageUrl : (fullPageUrl || cachedContentRef.current.fullPageUrl);

  // Handle ESC key and strict background scroll lock
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    const origBodyOverflow = document.body.style.overflow;
    const origHtmlOverflow = document.documentElement.style.overflow;
    const origBodyTouch = document.body.style.touchAction;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = origBodyOverflow;
      document.documentElement.style.overflow = origHtmlOverflow;
      document.body.style.touchAction = origBodyTouch;
    };
  }, [isOpen, onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] font-poppins">
          {/* Frosted Glass Backdrop with Smooth Fade */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.26, ease: 'easeOut' }}
            className="fixed inset-0 bg-slate-950/55 backdrop-blur-[6px] overscroll-contain"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Slide-Over Curtain Drawer Panel with Spring Physics */}
          <motion.div
            initial={{ x: '100%', opacity: 0.92 }}
            animate={{
              x: 0,
              opacity: 1,
              transition: {
                type: 'spring',
                damping: 32,
                stiffness: 340,
                mass: 0.82,
              },
            }}
            exit={{
              x: '100%',
              opacity: 0.92,
              transition: {
                duration: 0.22,
                ease: [0.32, 0, 0.67, 0],
              },
            }}
            className={`fixed inset-y-0 right-0 z-50 w-full ${resolvedWidth} bg-card border-l border-border/80 shadow-[-20px_0_50px_rgba(0,0,0,0.35)] flex flex-col font-poppins overscroll-contain`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Accent Gradient Line */}
            <div className="h-1 w-full bg-gradient-to-r from-primary via-primary/70 to-blue-500 shrink-0" />

            {/* Drawer Sticky Header */}
            <div className="flex items-center justify-between p-3.5 sm:p-4 border-b border-border/70 bg-card/95 backdrop-blur-md shrink-0">
              <div className="flex items-center gap-3 min-w-0 pr-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-base sm:text-lg font-black tracking-tight text-foreground truncate font-poppins">
                      {activeTitle}
                    </h2>
                    {activeBadge && <div className="shrink-0">{activeBadge}</div>}
                  </div>
                  {activeSubtitle && (
                    <p className="text-xs text-foreground-secondary font-medium mt-0.5 truncate font-poppins">
                      {activeSubtitle}
                    </p>
                  )}
                </div>
              </div>

              {/* Action Controls */}
              <div className="flex items-center gap-2 shrink-0">
                {activeFullPageUrl && (
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        onClose();
                        navigate(activeFullPageUrl);
                      }}
                      className="rounded-xl font-bold text-xs h-8 font-poppins cursor-pointer border-border hover:bg-muted"
                      title="Expand to Full Page"
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                      <span className="hidden sm:inline">Open Full Page</span>
                    </Button>
                  </motion.div>
                )}

                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.15 }}
                  onClick={onClose}
                  className="h-8 w-8 rounded-xl border border-border bg-surface hover:bg-muted flex items-center justify-center text-foreground-secondary hover:text-foreground cursor-pointer transition-colors shadow-2xs"
                  title="Close Drawer (Esc)"
                >
                  <X className="h-4 w-4" />
                </motion.button>
              </div>
            </div>

            {/* Scrollable Drawer Body with Subtle Stagger Fade-In */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.24, delay: 0.08, ease: 'easeOut' }}
              className="flex-1 overflow-y-auto overscroll-contain custom-scrollbar p-3.5 sm:p-5 space-y-4"
            >
              {activeChildren}
            </motion.div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};
